import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import IncrementBadge from '@/components/Badge/IncrementBadge';
import {
  CHART_FREQUENCY,
  CHART_METRIC_VIEW_TYPE,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TOTAL_ROW_KEY,
  CHART_X_TOTAL_KEY,
} from '@/entities/chart/model/constants';
import useChartTableMeta from '@/features/(charts)/chart-detail/hooks/use-chart-table-meta';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import TableCellRenderer from '@/features/(charts)/chart-renderer/ui/table-cell-element';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { getTimezoneDayjs } from '@/utils/time';
import { calIncrementAmount, getPercentStringFormat } from '@/utils/util';

import { useOpenChartClickDialog } from '../../hooks/use-open-chart-click-dialog';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
};

const useChartTotalTableData = (props: Props) => {
  const openChartClickDialog = useOpenChartClickDialog();
  const { chart } = props;
  const tableData: ChartSingleTableRes = chartUtils.getSortedRowItems(
    chart,
    props.tableData,
    chart.selected_field.items || [],
  ) as ChartSingleTableRes;
  const getLabel = useChartLabel(tableData.field_item_meta);

  const { baseColumns, getLevelRowMap, getTotalRowMap } =
    useChartTableMeta(props);

  const currentDateRange = chartUtils.getTimeRangeByTerm(
    getTimezoneDayjs(),
    chart.target.term,
  );

  const dateLabels = chartUtils.getDateLabelsByUnit({
    unit: chart.target.term.unit,
    dateRange: currentDateRange,
  });

  const compareDateRange = chartUtils.getCompareTimeRangeByTerm(
    getTimezoneDayjs(),
    chart.target.term,
    chart.target.compare_term,
  );

  const formatter = chartUtils.getFormatByFrequency(
    chart.historical.frequency || CHART_FREQUENCY.MONTH,
  );

  const onCellClick = useCallback(
    (
      x: StringNumberValue | null,
      y: StringNumberValue | null,
      comparedTarget: boolean,
    ) => {
      const modalOptions: ChartFeedbackListModalOptions<CellFeedbackListModalOptions> =
        {
          chart: chart,
          isAll: false,
          cell: {
            compared_target: comparedTarget,
            series_index: 0,
            item_x: x || '',
            item_x_name: getLabel(chart.field.type, x || ''),
            item_y: y || '',
            item_y_name: formatter(getTimezoneDayjs(y?.toString() || '')),
          },
          dashboardCardInfo: props.dashboardCardInfo,
        };
      openChartClickDialog({
        alias: JSON.stringify(modalOptions),
        type: DETAIL_MODAL_TYPE.CHART,
        idx: 0,
        options: modalOptions,
      });
    },
    [chart, formatter, getLabel, openChartClickDialog, props.dashboardCardInfo],
  );

  const isPercentage =
    chart.series[0]?.options?.view_type === CHART_METRIC_VIEW_TYPE.PERCENTAGE;

  const statistics: ColorScaleStatistics = useMemo(() => {
    if (
      typeof chart.series[0]?.options?.scale_min === 'number' &&
      typeof chart.series[0]?.options?.scale_max === 'number'
    ) {
      return {
        max: chart.series[0]?.options?.scale_max,
        min: chart.series[0]?.options?.scale_min,
      };
    }
    const result = {
      max: 0,
      min: 0,
    };

    (tableData.tables[0] || []).forEach((x) => {
      if (x.value > result.max) result.max = x.value;
      if (x.value < result.min) result.min = x.value;
    });

    (tableData.compare_tables?.[0] || []).forEach((x) => {
      if (x.value > result.max) result.max = x.value;
      if (x.value < result.min) result.min = x.value;
    });

    return result;
  }, [chart.series, tableData.compare_tables, tableData.tables]);

  const columns = useMemo((): ColumnsType<any> => {
    const columns: ColumnsType<any> = [...baseColumns];

    const renderer = (
      value: number,
      rowIndex: number,
      columnKey: StringNumberValue,
      comparedTarget: boolean,
      totalCell?: boolean,
    ) => {
      return {
        props: {
          style: {
            backgroundColor:
              !totalCell && chart.series[0]?.options?.color_scale
                ? chartUtils.getColorScaleColor(
                    statistics,
                    value,
                    chart.series[0]?.options?.color_scale,
                    isPercentage,
                  )
                : 'transparent',
          },
        },
        children: (
          <TableCellRenderer
            value={value}
            isPercentage={isPercentage}
            onClick={() => {
              onCellClick(
                (tableData.tables[0] || [])[rowIndex]?.x || null,
                columnKey || null,
                comparedTarget,
              );
            }}
            totalCell={totalCell}
          />
        ),
      };
    };
    if (chart.target.is_compare) {
      const compareColumns: ColumnsType<any> = [
        {
          title: 'Previous',
          dataIndex: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
          key: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
          render: (value: number, row: any, index: number) => {
            return renderer(
              value,
              index,
              compareDateRange[0].toJSON(),
              false,
              row.key === CHART_TOTAL_ROW_KEY,
            );
          },
        },
        {
          title: 'Current',
          dataIndex: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          key: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          render: (value: number, row: any, index: number) => {
            return renderer(
              value,
              index,
              currentDateRange[0].toJSON(),
              false,
              row.key === CHART_TOTAL_ROW_KEY,
            );
          },
        },
        {
          title: '%Change',
          dataIndex: 'change',
          key: 'change',
          render: (text: number, row: any) => {
            if (row.key === CHART_TOTAL_ROW_KEY) {
              return null;
            }
            return <IncrementBadge amount={text} />;
          },
        },
      ];
      columns.push(...compareColumns);
    } else {
      const start = dateLabels[0];
      const end = dateLabels[dateLabels.length - 1];

      columns.push({
        title: `${start} - ${end}`,
        dataIndex: CHART_X_TOTAL_KEY,
        key: CHART_X_TOTAL_KEY,
        render: (value: number, row: any, index: number) => {
          return renderer(
            value,
            index,
            currentDateRange[0].toJSON(),
            false,
            row.key === CHART_TOTAL_ROW_KEY,
          );
        },
      });
    }
    return columns;
  }, [
    baseColumns,
    chart.series,
    chart.target.is_compare,
    compareDateRange,
    currentDateRange,
    dateLabels,
    isPercentage,
    onCellClick,
    statistics,
    tableData.tables,
  ]);

  const dataSource = useMemo(() => {
    const isPercentage =
      chart.series[0]?.options?.view_type === CHART_METRIC_VIEW_TYPE.PERCENTAGE;

    const currentTableTotal = (tableData.tables[0] || []).reduce(
      (acc, curr) => {
        acc += curr.value;
        return acc;
      },
      0,
    );

    if (!chart.historical.value) {
      // Total compare
      if (chart.target.is_compare) {
        const compareTableTotal =
          (tableData.compare_tables?.[0] || []).reduce((acc, curr) => {
            acc += curr.value;
            return acc;
          }, 0) || 0;
        const result: Record<string, StringNumberValue | null>[] = (
          tableData.tables[0] || []
        ).map((item) => {
          const levelRowMap = getLevelRowMap(item.x);
          const currentData = item;
          const compareData = (tableData.compare_tables?.[0] || []).find(
            (d) => d.x === item.x,
          );

          const currnetRowTotal = currentData?.value || 0;
          const compareRowTotal = compareData?.value || 0;

          return {
            key: currentData.x,
            ...levelRowMap,
            [CHART_TOOLTIP_TARGET_TYPE.CURRENT]: isPercentage
              ? Number(
                  getPercentStringFormat(currentTableTotal, currnetRowTotal, 2),
                )
              : currnetRowTotal,
            [CHART_TOOLTIP_TARGET_TYPE.COMPARE]: isPercentage
              ? Number(
                  getPercentStringFormat(compareTableTotal, compareRowTotal, 2),
                )
              : compareRowTotal,
            change: calIncrementAmount(currnetRowTotal, compareRowTotal),
          };
        });

        result.push({
          key: CHART_TOTAL_ROW_KEY,
          ...getTotalRowMap(),
          [CHART_TOOLTIP_TARGET_TYPE.CURRENT]: isPercentage
            ? Number(
                getPercentStringFormat(currentTableTotal, currentTableTotal, 2),
              )
            : currentTableTotal,
          [CHART_TOOLTIP_TARGET_TYPE.COMPARE]: isPercentage
            ? Number(
                getPercentStringFormat(compareTableTotal, compareTableTotal, 2),
              )
            : compareTableTotal,
          change: '-',
          [CHART_X_TOTAL_KEY]: isPercentage
            ? Number(
                getPercentStringFormat(currentTableTotal, currentTableTotal, 2),
              )
            : currentTableTotal,
        });

        return result;
      }

      const result: Record<string, StringNumberValue>[] = (
        tableData.tables[0] || []
      ).map((item) => {
        const levelRowMap = getLevelRowMap(item.x);
        const rowTotal = item?.value || 0;
        return {
          key: item.x,
          ...levelRowMap,
          [CHART_X_TOTAL_KEY]: isPercentage
            ? Number(getPercentStringFormat(currentTableTotal, rowTotal, 2))
            : rowTotal,
        };
      });

      result.push({
        key: CHART_TOTAL_ROW_KEY,
        ...getTotalRowMap(),
        [CHART_X_TOTAL_KEY]: isPercentage
          ? Number(
              getPercentStringFormat(currentTableTotal, currentTableTotal, 2),
            )
          : currentTableTotal,
      });
      return result;
    }
  }, [
    chart.series,
    chart.historical.value,
    chart.target.is_compare,
    getLevelRowMap,
    getTotalRowMap,
    tableData.compare_tables,
    tableData.tables,
  ]);

  return {
    columns,
    dataSource,
  };
};

export default useChartTotalTableData;
